/**
 * Iter64d — Reconciliere date istorice (admin): PREVIEW per rând + „Aplică" explicit,
 * totaluri pe perimetru (global vs. CSD interval) și lacune de atribuire comision.
 * Nimic nu se aplică automat.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { formatRoDate } from "../lib/dates";
import { IdentityReviewDialog, DECISION_LABELS } from "../components/IdentityReviewDialog";
import { ImportRowLinkDialog } from "../components/ImportRowLinkDialog";
import { ImportRowNewContractDialog } from "../components/ImportRowNewContractDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = `${API_URL}/admin/data-reconciliation`;

const KIND_META = {
  signed_date_overwrite: { label: "Semnare suprascrisă (SF → Easy)", hint: "signed_date curent = valoarea Easy „Data contract”, iar Salesforce avea altă valoare." },
  commodity_mismatch: { label: "Energie ≠ produs", hint: "commodity contrazice product_kind/produs (ex. electric cu „PPC Relaxat Gaz”)." },
  csd_before_signed: { label: "CSD anterior semnării", hint: "Suspiciuni, nu erori demonstrate. Propunere doar dacă există valoare anterioară importului Easy." },
  identity_review: { label: "Identitate de verificat", hint: "ID Easy suprascris pe POD sau rânduri de import fără dovadă de identitate (POD + client + semnare apropiată nu demonstrează aceeași vânzare). Fiecare caz cere o decizie explicită (legare rând / corecție ID / revizie / contract succesiv / rămâne la verificare); rândul sursă, candidații și motivul sunt păstrate." },
};

const StatBox = ({ label, g, p }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`stat-${label}`}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-lg font-semibold text-slate-900">{p ?? "—"} <span className="text-xs font-normal text-slate-400">în perimetru · {g ?? "—"} global</span></p>
  </div>
);

export default function DataReconciliation() {
  const [csdFrom, setCsdFrom] = useState("2026-08-01");
  const [csdTo, setCsdTo] = useState("2026-08-31");
  const [stats, setStats] = useState(null);
  const [kind, setKind] = useState("signed_date_overwrite");
  const [preview, setPreview] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState({});
  const [applied, setApplied] = useState({});
  const [noCsd, setNoCsd] = useState(false);
  const [identityFor, setIdentityFor] = useState(null);
  const [linkItem, setLinkItem] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const reqRef = useState({ n: 0 })[0];

  const q = noCsd ? "no_csd=true" : `csd_from=${csdFrom}&csd_to=${csdTo}`;

  const loadStats = useCallback(async () => {
    setLoading(true);
    try { setStats((await axios.get(`${BASE}/stats?${q}`)).data); }
    catch { toast.error("Nu am putut încărca totalurile"); }
    finally { setLoading(false); }
  }, [q]);

  const loadPreview = useCallback(async (k) => {
    setLoading(true);
    setPreview(null); // fără rezultate rămase din cereri anterioare
    const my = ++reqRef.n;
    try {
      const r = await axios.get(`${BASE}/preview?kind=${k}&${q}&limit=5000`);
      if (my === reqRef.n) setPreview({ ...r.data, _q: q });
    } catch { toast.error("Nu am putut încărca previzualizarea"); }
    finally { if (my === reqRef.n) setLoading(false); }
  }, [q, reqRef]);

  // schimbarea perioadei invalidează lista afișată
  useEffect(() => { setPreview(null); setGaps(null); }, [q]);

  const loadGaps = useCallback(async () => {
    setLoading(true);
    try { setGaps((await axios.get(`${BASE}/commission-gaps?${q}`)).data); }
    catch { toast.error("Nu am putut încărca lacunele"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const apply = async (item) => {
    if (!item.proposed_value) return;
    let identityConfirmed = false;
    if (item.ambiguous) {
      if (!item.apply_allowed) { setIdentityFor(item.contract_id); return; }
      identityConfirmed = window.confirm(
        `Identitate decisă: ${DECISION_LABELS[item.identity_decision?.decision] || "—"}\n„${item.identity_decision?.justification || ""}”\n\nAplici propunerea pe același contract?`);
      if (!identityConfirmed) return;
    }
    if (!window.confirm(`Aplici pe contractul ${item.pod}: ${item.field} ${item.current_value} → ${item.proposed_value}?\nJustificare: ${item.justification}`)) return;
    setApplying((a) => ({ ...a, [item.contract_id]: true }));
    try {
      const r = await axios.post(`${BASE}/apply`, {
        contract_id: item.contract_id, field: item.field, proposed_value: item.proposed_value,
        justification: item.justification, kind, expected_current_value: item.current_value,
        ambiguous: !!item.ambiguous, identity_confirmed: identityConfirmed,
      });
      setApplied((a) => ({ ...a, [item.contract_id]: r.data }));
      toast.success("Aplicat (înregistrat în istoric + audit)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la aplicare");
    } finally {
      setApplying((a) => ({ ...a, [item.contract_id]: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="data-reconciliation-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Reconciliere date istorice</h1>
        <p className="text-sm text-slate-500">Previzualizare per rând — valoare actuală, propusă, sursă, justificare, impact comision. Nimic nu se aplică fără click explicit.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-slate-500">CSD de la</label><Input type="date" value={csdFrom} onChange={(e) => setCsdFrom(e.target.value)} data-testid="recon-csd-from" /></div>
        <div><label className="text-xs text-slate-500">CSD până la</label><Input type="date" value={csdTo} onChange={(e) => setCsdTo(e.target.value)} data-testid="recon-csd-to" /></div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 pb-2"><input type="checkbox" checked={noCsd} onChange={(e) => setNoCsd(e.target.checked)} data-testid="recon-no-csd" /> doar înregistrări fără CSD</label>
        <Button size="sm" variant="outline" onClick={loadStats} disabled={loading} data-testid="recon-reload-stats">Recalculează totalurile</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-stats">
          <StatBox label="CSD < semnare" g={stats.global.csd_before_signed} p={noCsd ? stats.no_csd?.csd_before_signed : stats.perimeter?.csd_before_signed} />
          <StatBox label="Semnare SF suprascrisă de Easy" g={stats.global.signed_date_overwrite_sf_by_easy} p={noCsd ? stats.no_csd?.signed_date_overwrite_sf_by_easy : stats.perimeter?.signed_date_overwrite_sf_by_easy} />
          <StatBox label="Energie ≠ produs" g={stats.global.commodity_mismatch} p={noCsd ? stats.no_csd?.commodity_mismatch : stats.perimeter?.commodity_mismatch} />
          <StatBox label="Modificări semnare Easy (toate)" g={stats.global.easy_signed_date_changes_total} p={stats.perimeter?.easy_signed_date_changes_total} />
        </div>
      )}

      <Tabs defaultValue="preview">
        <TabsList>
          <TabsTrigger value="preview" data-testid="tab-recon-preview">Previzualizare remedieri</TabsTrigger>
          <TabsTrigger value="gaps" data-testid="tab-recon-gaps" onClick={() => !gaps && loadGaps()}>Lacune comision</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {Object.entries(KIND_META).map(([k, m]) => (
              <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => { setKind(k); loadPreview(k); }} data-testid={`recon-kind-${k}`}>{m.label}</Button>
            ))}
          </div>
          <p className="text-xs text-slate-500">{KIND_META[kind].hint}</p>
          {!preview && !loading && <p className="text-xs text-slate-400">Alege o categorie pentru a încărca previzualizarea pentru perioada selectată.</p>}
          {preview && preview.kind === kind && preview._q === q && (
            <Card><CardContent className="p-0 overflow-x-auto">
              <p className="px-3 py-2 text-xs text-slate-500" data-testid="recon-preview-total">
                {preview.total} rânduri{preview.perimeter?.no_csd ? " fără CSD" : preview.perimeter ? ` cu CSD în ${formatRoDate(preview.perimeter.csd_from)} – ${formatRoDate(preview.perimeter.csd_to)}` : " (global)"}
                {preview.without_csd > 0 && !preview.perimeter?.no_csd && <span className="ml-2 text-amber-700" data-testid="recon-preview-without-csd">· {preview.without_csd} fără CSD</span>}
              </p>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left px-2 py-1.5">Înregistrare</th><th className="text-left px-2">Câmp</th><th className="text-left px-2">Actual</th>
                  <th className="text-left px-2">Propus</th><th className="text-left px-2">Lună comision</th><th className="text-left px-2">Justificare</th><th className="px-2"></th>
                </tr></thead>
                <tbody>
                  {preview.items.map((it) => (
                    <tr key={it.item_key || `${it.contract_id}-${it.field}`} className="border-t border-slate-100 align-top" data-testid={`recon-row-${it.item_key || it.contract_id}`} data-origin={it.origin || ""} data-batch={it.evidence?.batch_id || ""} data-row={it.evidence?.row ?? ""}>
                      <td className="px-2 py-1.5">
                        {it.sale_id ? <Link className="text-blue-600 hover:underline font-mono" to={`/sales/${it.sale_id}`}>{it.pod}</Link> : <span className="font-mono">{it.pod}</span>}
                        <div className="text-slate-500">{it.client} · SF {it.sf_contract_id || "—"} · Easy {it.easy_contract_id || "—"}</div>
                      </td>
                      <td className="px-2 py-1.5 font-mono">{it.field}</td>
                      <td className="px-2 py-1.5" data-testid={`recon-current-${it.contract_id}`}>{it.field.endsWith("date") ? formatRoDate(it.current_value) : it.current_value}<div className="text-slate-400">{it.current_source}</div>
                        {it.historical_import && <div className="text-slate-500 mt-0.5" data-testid={`recon-hist-${it.contract_id}`}>import istoric: <span className="font-mono">{it.historical_import.old_id}</span> → <span className="font-mono">{it.historical_import.new_id}</span> · {it.historical_import.file} · {formatRoDate(it.historical_import.date)}{!it.historical_import.in_force && <span className="text-rose-600"> · nu mai e în vigoare</span>}</div>}
                      </td>
                      <td className="px-2 py-1.5">{it.proposed_value ? (it.field.endsWith("date") ? formatRoDate(it.proposed_value) : it.proposed_value) : <span className="text-slate-400">fără propunere</span>}<div className="text-slate-400">{it.proposed_source}</div></td>
                      <td className="px-2 py-1.5" data-testid={`recon-month-${it.contract_id}`}>{it.commission_month || <Badge className="bg-slate-100 text-slate-600 border-0">fără CSD</Badge>}<div className="text-slate-400">{it.commission_month_impact}</div></td>
                      <td className="px-2 py-1.5 max-w-md text-slate-600">{it.justification}{it.ambiguous && <Badge className="ml-1 bg-amber-100 text-amber-800 border-0">ambiguu</Badge>}</td>
                      <td className="px-2 py-1.5">
                        {applied[it.contract_id] ? <Badge className="bg-emerald-100 text-emerald-700 border-0">aplicat</Badge> : (
                          <div className="flex flex-col gap-1">
                            {it.ambiguous && (
                              <Button size="sm" variant={it.identity_decision ? "outline" : "default"} onClick={() => setIdentityFor(it.contract_id)} data-testid={`recon-identity-${it.contract_id}`}>
                                {it.identity_decision ? "Dosar identitate" : "Verifică identitatea"}
                              </Button>
                            )}
                            {it.origin === "import_conflict" && it.link_allowed && (
                              <Button size="sm" variant="secondary" onClick={() => setLinkItem(it)} data-testid={`recon-link-row-${it.contract_id}-${it.evidence?.batch_id}-${it.evidence?.row}`}>Leagă rândul la contract</Button>
                            )}
                            {it.origin === "import_conflict" && it.new_contract_allowed && (
                              <Button size="sm" variant="outline" onClick={() => setNewItem(it)} data-testid={`recon-new-contract-row-${it.contract_id}-${it.evidence?.batch_id}-${it.evidence?.row}`}>Contract nou confirmat</Button>
                            )}
                            {it.identity_decision && <span className="text-[11px] text-slate-500" data-testid={`recon-decision-${it.contract_id}`}>decis: {DECISION_LABELS[it.identity_decision.decision] || it.identity_decision.decision}</span>}
                            {it.field !== "identity" && (
                              <Button size="sm" variant="outline" disabled={!it.apply_allowed || applying[it.contract_id]} onClick={() => apply(it)} data-testid={`recon-apply-${it.contract_id}`}
                                title={it.ambiguous && !it.apply_allowed ? "Necesită o decizie de identitate înregistrată („același contract”)" : undefined}>
                                Aplică
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>

        <IdentityReviewDialog contractId={identityFor} open={!!identityFor} onClose={() => setIdentityFor(null)} onDecided={() => loadPreview(kind)} />
        <ImportRowLinkDialog item={linkItem} open={!!linkItem} onClose={() => setLinkItem(null)} onLinked={() => loadPreview(kind)} />
        <ImportRowNewContractDialog item={newItem} open={!!newItem} onClose={() => setNewItem(null)} onApplied={() => loadPreview(kind)} />

        <TabsContent value="gaps" className="space-y-3">
          {gaps && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="gaps-summary">
                <Badge variant="outline">Total: {gaps.total}</Badge>
                <Badge variant="outline">Fără agent în sursă: {gaps.summary.no_raw_agent_in_source}</Badge>
                <Badge variant="outline">Semnatar prezent, regulă limitată la magazine: {gaps.summary.signatory_present_rule_scoped}</Badge>
                <Badge variant="outline">Agent brut nemapat: {gaps.summary.no_agent}</Badge>
                <Badge variant="outline">Agent fără agenție: {gaps.summary.agent_without_agency}</Badge>
                <Badge variant="outline">Agenție diferită de agent: {gaps.summary.agency_changed_vs_agent}</Badge>
              </div>
              {gaps.notes.map((n, i) => <p key={i} className="text-xs text-slate-500">• {n}</p>)}
              <Card><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600"><tr>
                    <th className="text-left px-2 py-1.5">Vânzare</th><th className="text-left px-2">CSD / lună</th><th className="text-left px-2">Canal · sursă</th>
                    <th className="text-left px-2">Agent brut</th><th className="text-left px-2">Motiv</th><th className="text-left px-2">Informația lipsă</th><th className="text-left px-2">Potriviri existente</th>
                  </tr></thead>
                  <tbody>
                    {gaps.items.map((it) => (
                      <tr key={it.sale_id} className="border-t border-slate-100 align-top" data-testid={`gap-row-${it.sale_id}`}>
                        <td className="px-2 py-1.5"><Link className="text-blue-600 hover:underline font-mono" to={`/sales/${it.sale_id}`}>{it.pod || it.sf_contract_id}</Link><div className="text-slate-500">{it.client}</div></td>
                        <td className="px-2 py-1.5">{formatRoDate(it.csd_date)}<div className="text-slate-500">{it.commission_month}</div></td>
                        <td className="px-2 py-1.5">{it.channel || "—"}<div className="text-slate-500">{it.import_source}</div></td>
                        <td className="px-2 py-1.5">{it.raw_agent || it.signatory || <span className="text-slate-400">—</span>}{it.raw_code && <div className="font-mono text-slate-500">{it.raw_code}</div>}</td>
                        <td className="px-2 py-1.5 max-w-xs text-slate-600">{it.reasons.join(" ")}</td>
                        <td className="px-2 py-1.5 text-slate-600">{it.missing.join(", ") || "—"}</td>
                        <td className="px-2 py-1.5">{it.suggestions.length ? it.suggestions.map((s) => <div key={s.agent_id}>{s.agent} <span className="text-slate-400">({s.agency || "fără agenție"} · {s.match})</span></div>) : <span className="text-slate-400">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
